import { action, computed, makeObservable, observable } from 'mobx';
import type React from 'react';
import { BindingProps, Message, refProxy, StoreNode } from '../../store';
import { Store } from '../../store/store';
import { Answer, JobModel, MomentModel, SyncStatus, Team, Transcript } from '../../entities';
import { PlayerState } from '../player/playerState';
import { RadioItemProps } from '../input';
import { findAllMatches } from '../../core'; //findAllAppearances
import { PlayerFramesetState } from '../playerFrameset/playerFramesetState';
import { MomentWordConfidence, TeamMemberRole, TranscriptFormat, UpdateMomentInput } from '@clipr/lib';
import { notifyError, notifyLoading, notifySuccess } from '../../services/notifications';
import { DownloadWindowState } from '../download/downloadWindowState';
import { PlayerSearchBarState } from '../playerShared/playerSearchBarState';
import {
  getMomentConfidenceLevel,
  getTranscriptConfidenceLevel,
  TranscriptConfidenceLevel,
} from './transcriptConfidence';

export type DeceptioScoreLevel =
  'truthful' |
  'likelyTruthful' |
  'deceptionPresent' |
  'deceptive';

export const DeceptioScoreLevels: Array<{
  level: DeceptioScoreLevel;
  label: string;
  rangeLabel: string;
  min: number;
  max: number;
}> = [
  { level: 'truthful', label: 'Truthful Statement', rangeLabel: '100 - 95%', min: 95, max: 100 },
  { level: 'likelyTruthful', label: 'Most Likely Truthful', rangeLabel: '94 - 82%', min: 82, max: 94 },
  { level: 'deceptionPresent', label: 'Deception Present', rangeLabel: '81 - 70%', min: 70, max: 81 },
  { level: 'deceptive', label: 'Deceptive Statement', rangeLabel: 'Below 70%', min: 0, max: 69 },
];

export const getDeceptioScoreLevel = (score?: number | null): DeceptioScoreLevel | null => {
  if (score === null || score === undefined)
    return null;

  return DeceptioScoreLevels.find(({ min, max }) => score >= min && score <= max)?.level ?? null;
};

// Only the deceptive bands are worth navigating to -- an investigator gains nothing from jumping to
// where the subject is truthful (vendor guidance). Order matters: this is the DOM traversal order.
export const DECEPTIVE_LEVELS: DeceptioScoreLevel[] = ['deceptionPresent', 'deceptive'];

export const isDeceptiveLevel = (level: DeceptioScoreLevel): boolean => DECEPTIVE_LEVELS.includes(level);

export const filterTargetAnswers = <T extends { actualSpeakerId: string | null }>(
  answers: T[], deceptioSpeakerId?: string | null,
): T[] => (deceptioSpeakerId ? answers.filter(a => a.actualSpeakerId === deceptioSpeakerId) : []);

export const suggestedSubjectSpeakerId = (
  answers: { actualSpeakerId: string | null }[]
): string | null => {
  if (answers.length === 0) return null;
  const first = answers[0].actualSpeakerId;
  if (first === null) return null;
  return answers.every(a => a.actualSpeakerId === first) ? first : null;
};

// A score badge colours the whole transcript row, so the answer behind it has to actually account
// for that row. Any-overlap matching painted a red "Deceptive" 48% across a row it met for 0.08s
// (6.4% of it) and a green 93% across a 69-word row it covered 5% of -- 3 of 8 badged rows on a
// measured job. Requiring the majority of the row keeps a fully-covered short row badged while
// dropping a row the answer merely brushes.
export const DECEPTIO_ROW_MATCH_MIN_RATIO = 0.5;

export const matchAnswerToRow = (
  row: Pick<MomentModel, 'actualSpeakerId' | 'startTime' | 'endTime'>,
  answers: Answer[], deceptioSpeakerId?: string | null,
): Answer | null => {
  // Gate on the ROW's speaker: a non-subject row inherits nothing, even by proximity.
  if (!deceptioSpeakerId || row.actualSpeakerId !== deceptioSpeakerId) return null;
  const rowDuration = row.endTime - row.startTime;
  const overlapping = answers
    .filter(a => typeof a.score === 'number')
    .map(a => ({ a, overlap: Math.max(0, Math.min(a.endTime, row.endTime) - Math.max(a.startTime, row.startTime)) }))
    // A zero-length row can never be "mostly covered"; fall back to requiring any overlap there.
    .filter(({ overlap }) => (rowDuration > 0 ?
      overlap / rowDuration >= DECEPTIO_ROW_MATCH_MIN_RATIO :
      overlap > 0))
    .sort((x, y) => y.overlap - x.overlap);
  return overlapping[0]?.a ?? null;   // no unbounded nearest fallback
};

export type PlayerTranscriptsLayout = 'vertical' | 'horizontal';

export enum PlayerSearchMode {
  Initialized = 'idle',
  Transcript = 'transcript',
  Ask = 'ask'
}

type Props = BindingProps<{
  jobId: string,
  player: PlayerState,
  frameset: PlayerFramesetState,
  teamId?: string | null,
  playerTutorialHighlightedComponents?: string[];
}>

export const TranscriptFormatItems: RadioItemProps[] = [
  { value: 'RTF', label: '.rtf' },
  { value: 'TXT', label: '.txt' }
];

type InlineTranscriptDescriptionFallback = {
  previousDescription: string | null;
  description: string;
};

export class PlayerTranscriptsState
  extends StoreNode {

  @observable isActiveTranscriptIntoView: boolean = false;
  @observable refreshScrollTimeout: ReturnType<typeof setTimeout> | null = null;
  @observable inlineEditingTranscriptId: string | null = null;
  @observable inlineEditValue: string = '';
  @observable isInlineEditSaving: boolean = false;
  @observable isTranscriptConfidenceVisible: boolean = true;
  @observable isTranscriptDeceptioVisible: boolean = false;
  @observable areTranscriptSpeakersVisible: boolean = true;
  readonly inlineTranscriptConfidenceFallbacks = observable.map<string, MomentWordConfidence[]>();
  readonly inlineTranscriptDescriptionFallbacks = observable.map<string, InlineTranscriptDescriptionFallback>();
  private inlineEditorElement: HTMLTextAreaElement | null = null;
  private pendingTranscriptJumpTimeout: ReturnType<typeof setTimeout> | null = null;

  readonly horizontalComponent = refProxy(this);
  readonly verticalComponent = refProxy(this);

  constructor(store: Store, props: Props) {
    super(store, props);
    makeObservable(this);

    this.playerSearchBarState = new PlayerSearchBarState(this.store, {
      jobId: () => this.jobId,
      frameset: () => this.frameset,
      teamId: () => this.teamId,
      refreshSearchMatches: () => this.refreshSearchMatches,
      clearSearch: () => this.clearSearch
    });

    this.autoListen('player', this.playerListener);
  }

  readonly playerSearchBarState: PlayerSearchBarState;

  // The header sits above the scroll box, but the analysis legend is sticky INSIDE it, so it
  // covers the top of the viewport too. Jumping to a hit without counting it lands the row under
  // the legend -- and turning Confidence on grows the legend by a whole row, which is when the
  // first deceptive statement disappears entirely.
  private getTopOcclusionHeight(sectionElement: Element): number {
    const header = sectionElement.querySelector('#user-player-transcript-header');
    const legend = sectionElement.querySelector('.transcript-confidence-legend');
    return (header?.clientHeight || 0) + (legend?.clientHeight || 0);
  }

  private scrollToMoment = (moment: MomentModel) => {

    if (!moment) 
      return;

    const sectionElement = this.activeComponent;

    if (!sectionElement) 
      return;

    const scrollElement = sectionElement.querySelector(`#user-player-transcript-scroll`);
    // const domMoment = this.domGetTranscriptMoment(moment.id);
    const domMoment = sectionElement.querySelector(`[data-moment="true"][data-moment-id="${moment.id}"]`);

    if (!domMoment || !scrollElement)
      return;

    const topOffsetElementHeight = this.getTopOcclusionHeight(sectionElement);   //header + sticky legend overlapping the scroll box
    const scrollElementTop = scrollElement.getBoundingClientRect().top;   //top value of the scroll element relative to the viewport
    const scrollElementScrollTop = scrollElement.scrollTop;               //how much is the element scrolled (always positive or 0)
    const domMomentTop = domMoment.getBoundingClientRect().top;           //top value of the scroll item element relative to the viewport
    let centerMomentOffset = (scrollElement.clientHeight - domMoment.clientHeight - topOffsetElementHeight) / 2;

    if (domMoment.clientHeight > (scrollElement.clientHeight - topOffsetElementHeight)) // when element is bigger than the visibile container it should be aligned on top
      centerMomentOffset = 0;

    // to determine the top value:
    const topValue =
      (domMomentTop - scrollElementTop) // determine the position of the scroll item relative to the top of the scroll element
      + scrollElementScrollTop          // add it to the already scrolled value
      - topOffsetElementHeight          // subtract the header offset
      - centerMomentOffset;             // subtract the position offset

    scrollElement.scrollTo({
      top: topValue,
      behavior: 'smooth'
    })
  }

  private scrollToSearchMatch = () => {

    const sectionElement = this.activeComponent;

    if (!sectionElement) 
      return;

    const scrollElement = sectionElement.querySelector(`#user-player-transcript-scroll`);
    // const domMoment = this.domGetTranscriptMoment(moment.id);
    const domSearchMatch = sectionElement.querySelector(`[data-match="true"][data-match-id="${this.searchNavIndex}"]`);

    if (!domSearchMatch || !scrollElement)
      return;

    const topOffsetElementHeight = this.getTopOcclusionHeight(sectionElement);   //header + sticky legend overlapping the scroll box
    const scrollElementTop = scrollElement.getBoundingClientRect().top;   //top value of the scroll element relative to the viewport
    const scrollElementScrollTop = scrollElement.scrollTop;               //how much is the element scrolled (always positive or 0)
    const domMomentTop = domSearchMatch.getBoundingClientRect().top;           //top value of the scroll item element relative to the viewport
    const centerMomentOffset = (scrollElement.clientHeight - domSearchMatch.clientHeight - topOffsetElementHeight) / 2;

    // to determine the top value:
    const topValue =
      (domMomentTop - scrollElementTop) // determine the position of the scroll item relative to the top of the scroll element
      + scrollElementScrollTop          // add it to the already scrolled value
      - topOffsetElementHeight          // subtract the header offset
      - centerMomentOffset;             // subtract the position offset

    scrollElement.scrollTo({
      top: topValue,
      behavior: 'smooth'
    })
  }

  private scrollToElement = (element: Element) => {
    const sectionElement = this.activeComponent;

    if (!sectionElement)
      return;

    const scrollElement = sectionElement.querySelector(`#user-player-transcript-scroll`);

    if (!scrollElement)
      return;

    const topOffsetElementHeight = this.getTopOcclusionHeight(sectionElement);
    const scrollElementTop = scrollElement.getBoundingClientRect().top;
    const scrollElementScrollTop = scrollElement.scrollTop;
    const elementTop = element.getBoundingClientRect().top;
    const centerElementOffset =
      (scrollElement.clientHeight - element.clientHeight - topOffsetElementHeight) / 2;

    scrollElement.scrollTo({
      top: (elementTop - scrollElementTop)
        + scrollElementScrollTop
        - topOffsetElementHeight
        - centerElementOffset,
      behavior: 'smooth'
    });
  }

  private isMomentIntoView(moment: MomentModel | null) {
    if (!moment)
      return;

    const scrollElement = document.querySelector(`#user-player-transcript-scroll`);
    const domMoment = this.domGetTranscriptMoment(moment.id);

    if (!domMoment || !scrollElement)
      return;

    const topOffsetElement = document.querySelector('#user-player-transcript-header')?.clientHeight || 0;
    const scrollElementTop = scrollElement.getBoundingClientRect().top;
    const scrollElementBottom = scrollElement.getBoundingClientRect().bottom;
    const domMomentTop = domMoment.getBoundingClientRect().top;
    const domMomentBottom = domMoment.getBoundingClientRect().bottom;

    return (domMomentTop >= (scrollElementTop + topOffsetElement)) && (domMomentBottom <= scrollElementBottom);
  }

  private domGetTranscriptMoment(id: string) {
    return document.querySelector(`[data-moment="true"][data-moment-id="${id}"]`);
  }

  private playerListener = (msg: Message<PlayerState>) => {
    const { type, payload } = msg;
    switch (type) {
      case 'activeTranscriptChange':
        this.scrollToMoment(payload.moment);
        break;

      default: break;
    }
  }

  @action
  scrollToCurrent() {

    const { momentView } = this.player;
    if (!momentView.activeTranscript)
      return;

    this.scrollToMoment(momentView.activeTranscript);
  }

  @action
  jumpToConfidenceLevel(level: TranscriptConfidenceLevel) {
    const sectionElement = this.activeComponent;

    if (!sectionElement)
      return;

    const target = sectionElement.querySelector(`[data-confidence-level="${level}"]`);
    if (!target)
      return;

    this.scrollToElement(target);
  }

  @action
  clearSearch = (invokePause?: boolean | null) => {
    this.refreshSearchMatches(invokePause);
    this.searchNavIndex = 1;
  }

  @action
  toggleTranscriptConfidenceVisibility() {
    this.isTranscriptConfidenceVisible = !this.isTranscriptConfidenceVisible;
  }

  @action
  toggleTranscriptDeceptioVisibility() {
    this.isTranscriptDeceptioVisible = !this.isTranscriptDeceptioVisible;
  }

  @action
  toggleTranscriptSpeakersVisibility() {
    this.areTranscriptSpeakersVisible = !this.areTranscriptSpeakersVisible;
  }

  @observable searchNavIndex: number = 1;
  @observable searchMatches = observable.map<string, MomentSearchAtom>();
  @computed get cliprGptEnabled() {    
    return (
      this.job?.hasAutomaticEnrichmentLevel && 
      this.job.isDone &&
      this.store.aiService.getCliprGptEnabled(this.team));
  }

  @computed get momentsSyncStatus(): SyncStatus | null {
    return this.job?.momentsSyncStatus || null;
  }

  @computed
  get searchValue(): string | null {
    return this.playerSearchBarState.searchQuery.value;
  }

  @computed get searchMatchItems(): MomentSearchAtom[] {
    return [...this.searchMatches.values()];
  }

  @computed get searchMatchesNumber(): number {
    return this.searchMatchItems.length;
  }

  @computed get selectedSearchMatch(): MomentSearchAtom | null {
    return this.getSearchMatchByGlobalId(this.searchNavIndex);
  }

  @computed get selectedMomentMatch(): MomentModel | null {
    const momentId = this.selectedSearchMatch?.momentId;
    return this.items?.find(moment => moment.id === momentId) || null;
  }

  @computed get showOverlay(): boolean {
    const { job } = this;

    return (job?.isTranscriptProcessing || job?.isTranscriptPending)
      ?? false;
  }

  @computed get isDeceptioExperienceAvailable(): boolean {
    return !!this.team?.deceptioAiEnabled;
  }

  @computed get isTranscriptDeceptioActive(): boolean {
    return this.isDeceptioExperienceAvailable && this.isTranscriptDeceptioVisible;
  }

  get deceptioSpeakerId(): string | undefined { return this.job?.deceptioSpeakerId; }

  @computed get targetAnswers(): MomentModel[] {
    return filterTargetAnswers(this.job?.answerMoments ?? [], this.deceptioSpeakerId);
  }

  getMomentMatches(momentId: string): MomentSearchAtom[] {
    return this.searchMatchItems.filter(momentMatch => momentMatch.momentId === momentId);
  }

  getMomentDescription(moment: MomentModel): string | null {
    const fallback = this.inlineTranscriptDescriptionFallbacks.get(moment.id);
    if (fallback && moment.description === fallback.previousDescription)
      return fallback.description;

    return moment.description;
  }

  getMomentWordConfidence(moment: MomentModel): MomentWordConfidence[] {
    if (moment.wordConfidenceNorm.length > 0 && moment.avgConfidence !== 1)
      return moment.wordConfidenceNorm;

    return this.inlineTranscriptConfidenceFallbacks.get(moment.id) ?? [];
  }

  getDeceptioAnswerForTranscript(moment: MomentModel): Answer | null {
    return matchAnswerToRow(moment, this.targetAnswers as Answer[], this.deceptioSpeakerId);
  }

  getDeceptioScoreForTranscript(moment: MomentModel): number | null {
    const answer = this.getDeceptioAnswerForTranscript(moment);
    // displayableDeceptioScore hides a bare 100 (zero findings): no analysis, nothing to show.
    return answer?.displayableDeceptioScore ?? null;
  }

  hasDeceptioConnectorAfterTranscript(moment: MomentModel): boolean {
    const score = this.getDeceptioScoreForTranscript(moment);
    const level = getDeceptioScoreLevel(score);
    if (typeof score !== 'number' || !level)
      return false;

    const currentIndex = this.items.findIndex(item => item.id === moment.id);
    const nextMoment = this.items[currentIndex + 1];
    if (!nextMoment)
      return false;

    const nextScore = this.getDeceptioScoreForTranscript(nextMoment);
    return nextScore === score && getDeceptioScoreLevel(nextScore) === level;
  }

  hasDeceptioConnectorBeforeTranscript(moment: MomentModel): boolean {
    const score = this.getDeceptioScoreForTranscript(moment);
    const level = getDeceptioScoreLevel(score);
    if (typeof score !== 'number' || !level)
      return false;

    const currentIndex = this.items.findIndex(item => item.id === moment.id);
    const previousMoment = this.items[currentIndex - 1];
    if (!previousMoment)
      return false;

    const previousScore = this.getDeceptioScoreForTranscript(previousMoment);
    return previousScore === score && getDeceptioScoreLevel(previousScore) === level;
  }

  // Counts the target speaker's scored Answers per band, NOT transcript sentences: reducing over
  // sentences once rendered a handful of answers as hundreds of occurrences.
  getDeceptioLevelCount(level: DeceptioScoreLevel): number {
    return this.targetAnswers.reduce((count, answer) => (
      getDeceptioScoreLevel(answer.displayableDeceptioScore) === level ? count + 1 : count
    ), 0);
  }

  // Cursor into the deceptive-answer sequence for next/previous stepping. -1 = before the first.
  @observable deceptioNavIndex: number = -1;

  // The deceptive answers only, sorted by time -- the sequence Prev/Next steps through. Stepping
  // through every scored answer (the previous behaviour) walked the investigator across truthful
  // ones too; the vendor's own Next button walks occurrences of deception, which is the whole
  // point of the control on a two-hour interview.
  @computed get navigableAnswers(): MomentModel[] {
    return this.targetAnswers
      .filter(a => {
        const level = getDeceptioScoreLevel(a.displayableDeceptioScore);
        return level !== null && isDeceptiveLevel(level);
      })
      .slice()
      .sort((a, b) => a.startTime - b.startTime);
  }

  private findNearestTranscriptRow(answer: MomentModel): MomentModel | null {
    if (!this.items.length)
      return null;
    // First try: exact overlap (answer and row share some time).
    const overlapping = this.items.find(row =>
      Math.max(row.startTime, answer.startTime) < Math.min(row.endTime, answer.endTime));
    if (overlapping)
      return overlapping;
    // Fallback: row with minimum distance to answer's start.
    return this.items.reduce((nearest, row) => {
      const dist = Math.abs(row.startTime - answer.startTime);
      const nearestDist = Math.abs(nearest.startTime - answer.startTime);
      return dist < nearestDist ? row : nearest;
    });
  }

  // Tapping a legend band takes the investigator to its first occurrence, and Next then walks on
  // from there -- so this sets the same cursor prev/next use rather than scrolling independently.
  // The call site outlived an earlier version of this method and threw on every click.
  @action
  jumpToDeceptioLevel(level: DeceptioScoreLevel) {
    const answers = this.navigableAnswers;
    const index = answers.findIndex(answer =>
      getDeceptioScoreLevel(answer.displayableDeceptioScore) === level);
    if (index === -1)
      return;

    this.deceptioNavIndex = index;
    const row = this.findNearestTranscriptRow(answers[index]);
    if (row)
      this.scrollToMoment(row);
  }

  @action
  jumpToNextDeceptive(moment?: MomentModel) {
    const answers = this.navigableAnswers;
    if (!answers.length)
      return;
    if (moment) {
      const currentAnswer = this.getDeceptioAnswerForTranscript(moment);
      const currentIndex = answers.findIndex(answer => answer.id === currentAnswer?.id);
      if (currentIndex === -1)
        return;
      this.deceptioNavIndex = currentIndex;
    }
    this.deceptioNavIndex = (this.deceptioNavIndex + 1) % answers.length;
    const answer = answers[this.deceptioNavIndex];
    const row = this.findNearestTranscriptRow(answer);
    if (row)
      this.scrollToMoment(row);
  }

  @action
  jumpToPrevDeceptive() {
    const answers = this.navigableAnswers;
    if (!answers.length)
      return;
    // Wrap: stepping back from the first (or from -1) lands on the last.
    this.deceptioNavIndex = (this.deceptioNavIndex - 1 + answers.length) % answers.length;
    const answer = answers[this.deceptioNavIndex];
    const row = this.findNearestTranscriptRow(answer);
    if (row)
      this.scrollToMoment(row);
  }

  openDeceptioAnalysisForTranscript(moment: MomentModel) {
    const answer = this.getDeceptioAnswerForTranscript(moment);
    if (!answer || !this.jobId)
      return;

    this.store.deceptioWindow.openAnalysis({
      jobId: this.jobId,
      momentId: answer.id,
      // The answer's own text, not the clicked row's: the vendor scored the whole answer and its
      // rule `ranges` are offsets into that string, so any other text misplaces every highlight.
      statement: answer.description ?? '',
    });
  }

  getConfidenceLevelCount(level: TranscriptConfidenceLevel): number {
    return this.items.reduce((count, moment) => {
      const wordMatches = this.getMomentWordConfidence(moment).filter(item =>
        getTranscriptConfidenceLevel(item.confidence) === level).length;

      if (wordMatches > 0)
        return count + wordMatches;

      return getMomentConfidenceLevel(moment.computedMinConfidence) === level ?
        count + 1 :
        count;
    }, 0);
  }

  isInlineEditingTranscript(momentId: string): boolean {
    return this.inlineEditingTranscriptId === momentId;
  }

  isInlineSavingTranscript(momentId: string): boolean {
    return this.isInlineEditingTranscript(momentId) && this.isInlineEditSaving;
  }

  clearPendingTranscriptJump() {
    if (!this.pendingTranscriptJumpTimeout)
      return;

    clearTimeout(this.pendingTranscriptJumpTimeout);
    this.pendingTranscriptJumpTimeout = null;
  }

  handleTranscriptClick(evt: React.MouseEvent, moment: MomentModel, isEditable: boolean) {
    if (this.isInlineEditingTranscript(moment.id))
      return;

    if (!isEditable) {
      this.player.invoke('jumpToMoment', { moment });
      return;
    }

    if (evt.detail > 1) {
      this.clearPendingTranscriptJump();
      return;
    }

    this.clearPendingTranscriptJump();
    this.pendingTranscriptJumpTimeout = setTimeout(action(() => {
      this.pendingTranscriptJumpTimeout = null;
      this.player.invoke('jumpToMoment', { moment });
    }), 300);
  }

  @action
  enterInlineTranscriptEdit(moment: MomentModel) {
    if (this.isInlineEditSaving)
      return;

    this.clearPendingTranscriptJump();
    this.inlineEditingTranscriptId = moment.id;
    this.inlineEditValue = this.getMomentDescription(moment) ?? '';
    this.isInlineEditSaving = false;
    this.inlineEditorElement = null;
    this.player.invoke('enterPause');
  }

  @action
  setInlineEditorElement = (element: HTMLTextAreaElement | null) => {
    if (!element || element === this.inlineEditorElement)
      return;

    this.inlineEditorElement = element;
    element.focus();
    element.select();
  }

  @action
  setInlineTranscriptEditValue(value: string) {
    this.inlineEditValue = value;
  }

  @action
  closeInlineTranscriptEdit() {
    this.inlineEditingTranscriptId = null;
    this.inlineEditValue = '';
    this.isInlineEditSaving = false;
    this.inlineEditorElement = null;
    this.player.invoke('exitPause');
  }

  @action
  cancelInlineTranscriptEdit() {
    this.closeInlineTranscriptEdit();
  }

  @action
  setInlineTranscriptSaving(value: boolean) {
    this.isInlineEditSaving = value;
  }

  @action
  setInlineTranscriptConfidenceFallback(momentId: string, wordConfidence: MomentWordConfidence[]) {
    if (wordConfidence.length === 0)
      return;

    this.inlineTranscriptConfidenceFallbacks.set(momentId, wordConfidence.slice());
  }

  @action
  setInlineTranscriptDescriptionFallback(
    momentId: string,
    previousDescription: string | null,
    description: string,
  ) {
    this.inlineTranscriptDescriptionFallbacks.set(momentId, {
      previousDescription,
      description,
    });
  }

  async saveInlineTranscriptEdit(moment: MomentModel): Promise<boolean> {
    if (!this.isInlineEditingTranscript(moment.id) || this.isInlineEditSaving)
      return false;

    if (!this.job) {
      notifyError(this, 'Could not update the transcript because the video is unavailable.');
      return false;
    }

    const description = this.inlineEditValue;
    const currentDescription = this.getMomentDescription(moment);

    if (!description.trim()) {
      notifyError(this, 'Transcript text cannot be empty.');
      return false;
    }

    if (description === (currentDescription ?? '')) {
      this.closeInlineTranscriptEdit();
      return true;
    }

    const input: UpdateMomentInput = {
      momentId: moment.id,
      jobId: moment.jobId,
      clipType: moment.clipType,
      description,
    };
    const wordConfidenceFallback = this.getMomentWordConfidence(moment);

    notifyLoading(this, 'Updating transcript');
    this.setInlineTranscriptSaving(true);

    const [, err] = await this.job.apiUpdateMoment(input);
    if (err) {
      this.setInlineTranscriptSaving(false);
      notifyError(this, 'Could not update the transcript because of an error.');
      return false;
    }

    this.setInlineTranscriptConfidenceFallback(moment.id, wordConfidenceFallback);
    this.setInlineTranscriptDescriptionFallback(moment.id, moment.description, description);
    this.refreshSearchMatches(false);
    notifySuccess(this, 'Transcript was updated.');
    this.closeInlineTranscriptEdit();

    return true;
  }

  handleInlineTranscriptEditKeyDown(evt: React.KeyboardEvent<HTMLTextAreaElement>, moment: MomentModel) {
    if (evt.key === 'Escape') {
      evt.preventDefault();
      this.cancelInlineTranscriptEdit();
      return;
    }

    if (evt.key === 'Enter' && (evt.metaKey || evt.ctrlKey)) {
      evt.preventDefault();
      void this.saveInlineTranscriptEdit(moment);
    }
  }

  handleInlineTranscriptEditorBlur(evt: React.FocusEvent<HTMLDivElement>, moment: MomentModel) {
    const nextFocusedElement = evt.relatedTarget as Node | null;
    if (nextFocusedElement && evt.currentTarget.contains(nextFocusedElement))
      return;

    void this.saveInlineTranscriptEdit(moment);
  }

  getSearchMatchByGlobalId(globalId: number): MomentSearchAtom | null {
    return this.searchMatches.get(globalId.toString()) || null;
  }

  @action
  insertSearchMatch(momentMatch: MomentSearchAtom) {
    this.searchMatches.set(momentMatch.globalIndex.toString(), momentMatch);
  }

  // search section 
  @action
  increaseSearchNavIndex() {
    if (this.searchNavIndex < this.searchMatchesNumber)
      this.searchNavIndex++;
    else
      this.searchNavIndex = 1;
    if (this.selectedMomentMatch)
      this.scrollToSearchMatch()
    // this.scrollToMoment(this.selectedMomentMatch)
  }

  @action
  decreaseSearchNavIndex() {
    if (this.searchNavIndex > 1)
      this.searchNavIndex--;
    else
      this.searchNavIndex = this.searchMatchesNumber;

    if (this.selectedMomentMatch)
      this.scrollToSearchMatch()
    // this.scrollToMoment(this.selectedMomentMatch)
  }

  @action
  refreshSearchMatches = (invokePause: boolean | null = true) => {
    if (invokePause)
      this.player.invoke('enterPause');
      
    this.searchMatches.clear();
    this.searchNavIndex = 1;

    if (!this.searchValue)
      return;

    let globalIndex = 0;

    this.items.forEach((moment, _index) => {

      if (!this.searchValue)
        return;

      const description = this.getMomentDescription(moment);
      if (!description)
        return;

      const indexPairList = findAllMatches(description, this.searchValue);
      indexPairList.forEach((indexPair, _i) => {
        globalIndex++;
        const momentMatch = new MomentSearchAtom({
          globalIndex: globalIndex,
          momentId: moment.id,
          indexPair: indexPair
        })
        this.insertSearchMatch(momentMatch);
      })
    });

    this.refreshScrollTimeout = setTimeout(() => this.scrollToSearchMatch(), 100);
  }
  //end search section
  @computed get teamId(): string | null {
    return this.resolvedProps.teamId ?? this.job?.ownerTeamId ?? null;
  }

  @computed get team(): Team | null {
    return this.store.teamManager.getTeam(this.teamId);
  }

  @computed get userTeamRole(): TeamMemberRole | null {
    const { user } = this.store;
    if (user) {
      return this.team?.getMember(user.id)?.role ?? null;
    }

    return null;
  }

  @computed get jobId() {
    return this.resolvedProps.jobId;
  }

  @computed get job(): JobModel | null {
    return this.store.maybeGetJob(this.jobId);
  }
  @computed get frameset(): PlayerFramesetState | null {
    return this.resolvedProps.frameset ?? null;
  }

  @computed get activeLayout(): PlayerTranscriptsLayout | null {
    const isTranscriptsVisible = this.frameset?.isSectionVisible('Transcripts');
    if (!isTranscriptsVisible)
      return null;

    if (this.frameset?.isWidgetMode)
      return 'horizontal';
    else
      return this.frameset?.isSectionVisible('Transcripts')
        && (this.frameset?.isSectionVisible('Index')
          || this.frameset?.isSectionVisible('Comments')) ?
        'horizontal' :
        'vertical';
  }

  @computed get activeComponent() {
    if (this.activeLayout) // active layout stated by the frameset
      return this.activeLayout === 'vertical' ?
        this.verticalComponent?.current :
        this.horizontalComponent?.current
    else { // if there is no active layout stated by the frameset, it should still try to find the active component
      if (this.horizontalComponent?.current)
        return this.horizontalComponent?.current
      if (this.verticalComponent?.current)
        return this.verticalComponent?.current
    }
    return null;
  }

  @computed
  get player(): PlayerState {
    return this.resolvedProps.player;
  }

  @computed
  get items(): Array<Transcript> {
    return this.player?.momentView?.transcriptMoments || [];
  }

  @computed
  get isSearchActive(): boolean {
    return !!this.playerSearchBarState.searchQuery.value;
  }

  @computed
  get activeTranscript(): Transcript | null {
    return this.player.momentView.activeTranscript;
  }

  @action
  updateIsTranscriptIntoView() {
    this.isActiveTranscriptIntoView = this.isMomentIntoView(this.player.momentView.activeTranscript) || false;
  }

  @action
  mounted() {
    this.updateIsTranscriptIntoView();
    this.scrollToCurrent();
  }

  @action
  handleScroll = () => {
    this.updateIsTranscriptIntoView();
  }

  @action
  openDownloadTranscript = async () => {

    const window = this.store.downloadWindow;

    const listener = (msg: Message<DownloadWindowState>) => {
      switch (msg.type) {
        case 'open':
          this.player.invoke('enterPause');
          break;
        case 'close':
          window.unlisten(listener);
          this.player.invoke('exitPause');
          break;
      }
    }

    window.listen(listener);

    window.open({
      message: 'Transcript format',
      onSubmit: async (format: string) => {
        if (!this.job)
          return null;

        const args = {
          format: format as TranscriptFormat
        };

        const [res, err] = await this.job.apiGetJobTranscript(args);

        if (!res || err) {
          notifyError(this, 'Transcript download failed.');
          return null;
        }

        return res;
      },
      formatItems: TranscriptFormatItems,
      title: 'Download Transcript'
    });

    return true;
  }

  @action
  reset() {
    this.playerSearchBarState.reset();
    this.clearSearch();
    this.inlineEditingTranscriptId = null;
    this.inlineEditValue = '';
    this.isInlineEditSaving = false;
    this.isTranscriptConfidenceVisible = true;
    this.isTranscriptDeceptioVisible = false;
    this.deceptioNavIndex = -1;
    this.inlineTranscriptConfidenceFallbacks.clear();
    this.inlineTranscriptDescriptionFallbacks.clear();
    this.inlineEditorElement = null;
    this.clearPendingTranscriptJump();

    if (this.refreshScrollTimeout)
      clearTimeout(this.refreshScrollTimeout);
  }
}

type MomentSearchItemProps = {
  globalIndex: number;
  momentId: string;
  indexPair: [number, number]
}

export class MomentSearchAtom {
  globalIndex: number;
  momentId: string;
  indexPair: [number, number];

  constructor(props: MomentSearchItemProps) {
    this.globalIndex = props.globalIndex;
    this.momentId = props.momentId;
    this.indexPair = props.indexPair;
  }

}
